import { z } from 'zod';

export const workflowAdmissionScopeSchema = z.enum(['unconfirmed', 'tenant', 'campaigns']);
export type WorkflowAdmissionScope = z.infer<typeof workflowAdmissionScopeSchema>;
export const workflowEffectStateSchema = z.enum(['not_started', 'started', 'delegated', 'succeeded', 'failed', 'uncertain']);
export const workflowStopModeSchema = z.enum(['new_entries', 'all_pending']);
export const workflowAdmissionReasonSchema = z.enum([
  'eligible', 'scope_unconfirmed', 'globally_disabled', 'locally_disabled',
  'dissociated', 'no_campaign', 'campaign_conflict', 'source_not_found',
  'source_attribution_unknown', 'before_activation', 'loop_guard',
  'filter_no_match', 'unsupported_capability', 'campaign_configuration_conflict', 'cancel_requested', 'runtime_failed',
  'campaign_inactive',
]);
export type WorkflowAdmissionReason = z.infer<typeof workflowAdmissionReasonSchema>;

export const workflowGraphSnapshotSchema = z.object({
  id: z.string().uuid(), tenant_id: z.string().uuid(), name: z.string(),
  version: z.number().int().positive(), trigger_type: z.string(),
  trigger_config: z.record(z.string(), z.unknown()),
  variables: z.record(z.string(), z.unknown()),
  nodes: z.array(z.object({
    id: z.string().min(1), type: z.string(),
    position: z.object({ x: z.number(), y: z.number() }),
    data: z.object({
      nodeType: z.string(), label: z.string(),
      config: z.record(z.string(), z.unknown()),
    }).passthrough(),
  })),
  edges: z.array(z.object({
    id: z.string(), source: z.string(), target: z.string(),
    // React Flow serializes an unnamed handle as null, not only as an omitted key.
    sourceHandle: z.string().nullish(), targetHandle: z.string().nullish(),
  })),
});
export type WorkflowGraphSnapshot = z.infer<typeof workflowGraphSnapshotSchema>;

export const workflowCapabilitiesSchema = z.object({
  can_edit_definition: z.boolean(), can_delete_definition: z.boolean(),
  can_toggle_global: z.boolean(), can_clone_editable: z.boolean(),
  can_manage_campaign_link: z.boolean(), can_convert_scope: z.boolean(),
  campaign_compatible: z.boolean(), unsupported_nodes: z.array(z.string()),
});
export type WorkflowCapabilities = z.infer<typeof workflowCapabilitiesSchema>;
export const workflowEffectiveStateSchema = z.object({
  eligible: z.boolean(), reason: workflowAdmissionReasonSchema,
  message: z.string(),
});
export const workflowCampaignSuspensionSchema = z.object({
  rule_id: z.string().uuid(), rule_name: z.string(), globally_active: z.boolean(),
  inherited: z.boolean(), suspended_by_link_id: z.string().uuid().nullable(),
  restored_at: z.union([z.string(), z.date()]).nullable(),
  effective_eligible: z.boolean(), reason: z.string(),
  applicable: z.boolean(), can_restore: z.boolean(),
  replacement_requires_confirmation: z.boolean(),
  candidate_link_ids: z.array(z.string().uuid()),
});
export const workflowCampaignBindingSchema = z.object({
  id: z.string().uuid(), workflow_id: z.string().uuid(), campaign_id: z.string().uuid(),
  workflow_name: z.string(), workflow_description: z.string().nullable(),
  trigger_type: z.string(), trigger_config: z.unknown(), nodes: z.unknown(),
  created_at: z.union([z.string(), z.date()]).nullable(),
  updated_at: z.union([z.string(), z.date()]).nullable(),
  generation: z.number().int().positive(), is_active: z.boolean(),
  dissociated_at: z.union([z.string(), z.date()]).nullable(),
  global_is_active: z.boolean(), definition_version: z.number().int(),
  admission_scope: workflowAdmissionScopeSchema,
  managed_by_org_id: z.string().uuid().nullable(),
  effective: workflowEffectiveStateSchema, capabilities: workflowCapabilitiesSchema,
  statistics: z.object({
    campaign_runs_count: z.number().int().nonnegative(), global_runs_count: z.number().int().nonnegative(),
    definition_legacy_runs_count: z.number().int().nonnegative(),
    last_run_status: z.string().nullable(), last_run_error: z.string().nullable(),
  }),
  rules: z.array(workflowCampaignSuspensionSchema),
  /** Generazioni precedenti con run ancora pendenti o in esecuzione. */
  pending_generations: z.array(z.object({
    generation: z.number().int().positive(), pending_runs: z.number().int().positive(),
  })),
});
export type WorkflowCampaignBinding = z.infer<typeof workflowCampaignBindingSchema>;
export const workflowCampaignStateSchema = z.object({
  success: z.literal(true),
  triggers: z.array(workflowCampaignBindingSchema),
  rules: z.array(workflowCampaignSuspensionSchema),
});
export type WorkflowCampaignState = z.infer<typeof workflowCampaignStateSchema>;
export type WorkflowCampaignRule = z.infer<typeof workflowCampaignSuspensionSchema>;

export const workflowRuleRestoreResultSchema = z.object({
  restored: z.literal(true), replayed: z.literal(false),
  global_activation_changed: z.literal(false), rule: workflowCampaignSuspensionSchema,
});
export type WorkflowRuleRestoreResult = z.infer<typeof workflowRuleRestoreResultSchema>;

export const workflowActionEffectSchema = z.object({
  id: z.string().uuid(), run_id: z.string().uuid(), node_id: z.string(),
  state: workflowEffectStateSchema,
  delegation_id: z.string().nullable(), provider_reference: z.string().nullable(),
  started_at: z.union([z.string(), z.date()]).nullable(),
  completed_at: z.union([z.string(), z.date()]).nullable(),
  error: z.string().nullable(),
});
export const workflowStopResultSchema = z.object({
  link_id: z.string().uuid(), generation: z.number().int().positive(),
  // false quando lo stop riguarda una generazione passata: il collegamento resta com'è.
  mode: workflowStopModeSchema, new_entries_disabled: z.boolean(),
  cancellation_requested_run_ids: z.array(z.string().uuid()),
  already_started_effects: z.array(workflowActionEffectSchema),
  prevented_run_ids: z.array(z.string().uuid()),
  note: z.string(),
});
export type WorkflowStopResult = z.infer<typeof workflowStopResultSchema>;
export const workflowLinkCommandSchema = z.object({
  workflow_id: z.string().uuid(),
  replace_rule_ids: z.array(z.string().uuid()).optional(),
}).strict();
export const workflowStopCommandSchema = z.object({
  mode: workflowStopModeSchema, generation: z.number().int().positive(),
}).strict();
export const workflowRestoreRuleSchema = z.object({ rule_id: z.string().uuid() }).strict();
export const workflowRuleReplacementsSchema = z.record(z.string().uuid(), z.array(z.string().uuid()));

export const workflowDefinitionStateSchema = z.object({
  id: z.string().uuid(), name: z.string(), version: z.number().int(),
  is_active: z.boolean(), admission_scope: workflowAdmissionScopeSchema,
  managed_by_org_id: z.string().uuid().nullable(),
  capabilities: workflowCapabilitiesSchema, effective: workflowEffectiveStateSchema,
  global_runs_count: z.number().int().nonnegative(), legacy_runs_count: z.number().int().nonnegative(),
  linked_campaigns_count: z.number().int().nonnegative(),
  allowed_conversion_scopes: z.array(z.enum(['tenant', 'campaigns'])),
});
export type WorkflowDefinitionState = z.infer<typeof workflowDefinitionStateSchema>;
export const workflowEditorDefinitionSchema = workflowGraphSnapshotSchema.extend({
  description: z.string().nullable(),
  is_active: z.boolean(),
  admission_scope: workflowAdmissionScopeSchema,
  managed_by_org_id: z.string().uuid().nullable(),
});
export const workflowEditorResponseSchema = z.object({
  workflow: workflowEditorDefinitionSchema.merge(workflowDefinitionStateSchema),
});
export type WorkflowEditorDefinition = z.infer<typeof workflowEditorResponseSchema>['workflow'];
export const workflowSavedResponseSchema = z.object({ workflow: workflowEditorDefinitionSchema });
export const workflowRunStartSchema = z.object({ run_id: z.string().uuid() });
export const workflowRunDetailSchema = z.object({
  id: z.string().uuid(), workflow_id: z.string().uuid(), status: z.string(),
  error_message: z.string().nullable(), started_at: z.union([z.string(), z.date()]).nullable(),
  completed_at: z.union([z.string(), z.date()]).nullable(), workflow_name: z.string(),
  action_effects: z.array(workflowActionEffectSchema),
});
export type WorkflowRunDetail = z.infer<typeof workflowRunDetailSchema>;
export const workflowSavedRunSchema = workflowRunDetailSchema.omit({ action_effects: true }).extend({
  provenance: z.object({
    kind: z.enum(['recorded', 'unknown_legacy']),
    campaign_id: z.string().uuid().nullable(), link_id: z.string().uuid().nullable(),
    generation: z.number().int().nullable(), definition_version: z.number().int().nullable(),
  }),
});
export type WorkflowSavedRun = z.infer<typeof workflowSavedRunSchema>;
export const workflowSavedRunsResponseSchema = z.object({
  runs: z.array(workflowSavedRunSchema), total: z.number().int(), limit: z.number().int(), offset: z.number().int(),
});
// Projection for the existing catalog, evaluated by the same binding policy as save.
export const workflowCatalogSelectionSchema = z.object({
  allowed: z.boolean(),
  reason: z.string(),
  message: z.string(),
});
export const workflowAdmissionDecisionQuerySchema = z.object({
  campaign_id: z.string().uuid().optional(), event_type: z.string().max(100).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50), offset: z.coerce.number().int().min(0).default(0),
});
export const workflowAdmissionDecisionSchema = z.object({
  id: z.string().uuid(), event_type: z.string(), occurrence_id: z.string(),
  campaign_id: z.string().uuid().nullable(), attribution_reason: z.string().nullable(),
  canonical_context: z.record(z.string(), z.unknown()),
  decided_at: z.union([z.date(), z.string()]),
  recipients: z.array(z.object({
    id: z.string().uuid(), workflow_id: z.string().uuid().nullable(), rule_id: z.string().uuid().nullable(),
    link_id: z.string().uuid().nullable(), generation: z.number().nullable(),
    admitted: z.boolean(), reason: z.string(),
    rule_execution_state: z.enum(['pending','started','succeeded','failed','uncertain']).nullable(),
  })),
});
export const workflowScopeCommandSchema = z.object({
  scope: z.enum(['tenant', 'campaigns']), expected_version: z.number().int().positive(),
}).strict();
export const workflowHistoryProvenanceSchema = z.object({
  kind: z.enum(['recorded', 'unknown_legacy']),
  campaign_id: z.string().uuid().nullable(), link_id: z.string().uuid().nullable(),
  generation: z.number().int().nullable(), event_id: z.string().uuid().nullable(),
  definition_version: z.number().int().nullable(),
});
export const workflowPageQuerySchema = z.object({
  search: z.string().max(255).optional(), status: z.string().max(32).optional(),
  trigger_type: z.string().max(100).optional(), campaign_id: z.string().uuid().optional(),
  workflow_id: z.string().uuid().optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  offset: z.coerce.number().int().min(0).default(0),
});

/** Trigger delle regole semplici CRM (CHECK crm_automation_rules_trigger_type_check). */
export const crmAutomationTriggerTypeSchema = z.enum(['lead_created', 'stage_changed', 'lead_won', 'lead_lost', 'lead_assigned']);
export type CrmAutomationTriggerType = z.infer<typeof crmAutomationTriggerTypeSchema>;

export const crmAutomationActionSnapshotSchema = z.object({
  id: z.string().default(''),
  type: z.enum(['send_notification', 'send_whatsapp', 'send_sms', 'send_email', 'send_gmail',
    'change_stage', 'assign_user', 'add_tag', 'create_activity', 'call_webhook', 'start_workflow']),
  config: z.object({
    message: z.string().optional(), title: z.string().optional(), target: z.string().optional(), user_id: z.string().optional(),
    priority: z.string().optional(), body: z.string().optional(), to: z.string().optional(),
    from_number: z.string().optional(), subject: z.string().optional(), from_account: z.string().optional(),
    account_id: z.string().optional(), template_id: z.string().optional(), sender_id: z.string().optional(),
    stage_id: z.string().optional(), tag: z.string().optional(), tags: z.array(z.string()).optional(),
    activity_type: z.string().optional(), description: z.string().optional(), url: z.string().optional(),
    method: z.string().optional(), headers: z.record(z.string(), z.string()).optional(),
    body_template: z.unknown().optional(), workflow_id: z.string().optional(),
    variables: z.record(z.string(), z.string()).optional(),
  }).passthrough(),
});
export const crmAutomationRuleSnapshotSchema = z.object({
  id: z.string().uuid(), name: z.string(), trigger_type: z.string(),
  trigger_config: z.object({
    to_stage_id: z.string().optional(), from_stage_id: z.string().optional(), pipeline_id: z.string().optional(),
  }).passthrough().default({}),
  conditions: z.array(z.object({
    id: z.string().default(''), field: z.string(),
    operator: z.enum(['equals', 'not_equals', 'contains', 'greater_than', 'less_than']), value: z.string(),
  })).default([]),
  actions: z.array(crmAutomationActionSnapshotSchema),
  is_active: z.boolean(), is_global: z.boolean(),
});
